import React from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  Stack,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { formatNumber } from "@tiny/utils/formatUtils";
import { WorkflowPublishImpactView } from "../../app/GeneratedApi";
import { getWaitingDelays } from "./publishDialog.helpers";
import { formatDelaySummary } from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/CustomNodes/Timing.helpers";

const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ mt: 2 }}>
    <Typography variant="label1" color="grey.100" fontWeight="bold">
      {title}
    </Typography>
    <Box sx={{ mt: 0.5 }}>{children}</Box>
  </Box>
);

// A node the backend could not name - label and landingLabel are both nullable
// - would otherwise render as an empty bold span, i.e. a sentence with a hole in
// it. Name it by what it is instead.
const NodeLabel = ({ children }: { children?: string | null }) => (
  <Typography component="span" variant="label1" fontWeight="bold">
    {children || "this step"}
  </Typography>
);

const WorkflowPublishConfirmationDialogContent = ({
  impact,
  splitWarnings = [],
  onPublish,
  onCancel,
}: {
  impact: WorkflowPublishImpactView;
  /** One message per split whose A/B test this publish restarts. */
  splitWarnings?: string[];
  /** Receives the delay nodes whose waiting contacts should be moved. */
  onPublish: (moveWaitingNodeIds: string[]) => void | Promise<void>;
  onCancel?: () => void;
}) => {
  const { added, removed, changed, triggerChanged, totalInFlight, hasChanges } =
    impact;

  const [isPublishing, setIsPublishing] = React.useState(false);

  // Changed delays with contacts waiting in them, each offered its own "move"
  // checkbox. Off by default; only offered when the backend says it can.
  const waitingDelays = getWaitingDelays(impact);
  const [moveWaitingNodeIds, setMoveWaitingNodeIds] = React.useState<string[]>(
    []
  );
  const toggleMoveWaiting = (nodeId: string, checked: boolean) =>
    setMoveWaitingNodeIds((prev) =>
      checked ? [...prev, nodeId] : prev.filter((id) => id !== nodeId)
    );

  const publish = async () => {
    setIsPublishing(true);
    try {
      await onPublish(moveWaitingNodeIds);
    } finally {
      setIsPublishing(false);
    }
  };

  const occupiedRemoved = removed.filter((node) => node.occupancy > 0);
  const strandedRemoved = occupiedRemoved.filter((node) => node.stranded);
  const relocatedRemoved = occupiedRemoved.filter((node) => !node.stranded);

  if (!hasChanges) {
    return (
      <>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="label1" color="grey.90">
            This workflow has no unpublished changes — what you see on the
            canvas is already live.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={onCancel}>
            Close
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="label1" color="grey.90">
          Publishing makes these changes live.{" "}
          {totalInFlight > 0
            ? `${pluralize(
                totalInFlight,
                "contact is",
                "contacts are"
              )} currently moving through this workflow and will continue on the updated version from their next step.`
            : "No contacts are currently moving through this workflow."}
        </Typography>

        {added.length > 0 && (
          <Section title={`Added (${added.length})`}>
            <Stack spacing={0.5}>
              {added.map((node) => (
                <Typography
                  key={node.nodeId}
                  variant="label1"
                  color="grey.90"
                  component="div"
                >
                  <NodeLabel>{node.label}</NodeLabel> — contacts that have not
                  passed this point yet will go through it.
                </Typography>
              ))}
            </Stack>
          </Section>
        )}

        {changed.length > 0 && (
          <Section title={`Updated (${changed.length})`}>
            <Stack spacing={0.5}>
              {changed.map((node) => (
                <Typography
                  key={node.nodeId}
                  variant="label1"
                  color="grey.90"
                  component="div"
                >
                  <NodeLabel>{node.label}</NodeLabel>
                </Typography>
              ))}
            </Stack>
          </Section>
        )}

        {removed.length > 0 && (
          <Section title={`Removed (${removed.length})`}>
            <Stack spacing={0.5}>
              {removed
                .filter((node) => node.occupancy === 0)
                .map((node) => (
                  <Typography
                    key={node.nodeId}
                    variant="label1"
                    color="grey.90"
                    component="div"
                  >
                    <NodeLabel>{node.label}</NodeLabel> — no contacts are
                    waiting here.
                  </Typography>
                ))}
              {relocatedRemoved.map((node) => (
                <Typography
                  key={node.nodeId}
                  variant="label1"
                  color="grey.90"
                  component="div"
                >
                  {pluralize(node.occupancy, "contact is", "contacts are")}{" "}
                  waiting at <NodeLabel>{node.label}</NodeLabel> — they will
                  continue at <NodeLabel>{node.landingLabel}</NodeLabel>.
                </Typography>
              ))}
            </Stack>
          </Section>
        )}

        {strandedRemoved.length > 0 && (
          <Alert variant="filled" severity="warning" sx={{ mt: 2 }}>
            <Stack spacing={0.5}>
              {strandedRemoved.map((node) => (
                <Typography
                  key={node.nodeId}
                  variant="label1"
                  component="div"
                  color="inherit"
                >
                  {pluralize(node.occupancy, "contact is", "contacts are")}{" "}
                  waiting at <NodeLabel>{node.label}</NodeLabel> and has nothing
                  left to move on to — their journey through this workflow will
                  end.
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {splitWarnings.length > 0 && (
          <Alert variant="filled" severity="warning" sx={{ mt: 2 }}>
            <Stack spacing={0.5}>
              {splitWarnings.map((warning) => (
                <Typography
                  key={warning}
                  variant="label1"
                  component="div"
                  color="inherit"
                >
                  {warning}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {triggerChanged && (
          <Alert variant="outlined" severity="info" sx={{ mt: 2 }}>
            <Typography variant="label1" color="inherit">
              The trigger has changed. This only affects who enters the workflow
              from now on — contacts already in the workflow are past the
              trigger and are not re-evaluated.
            </Typography>
          </Alert>
        )}

        {waitingDelays.length > 0 ? (
          <Section
            title={`You changed ${pluralize(
              waitingDelays.length,
              "delay that has",
              "delays that have"
            )} contacts waiting in ${
              waitingDelays.length === 1 ? "it" : "them"
            }`}
          >
            <Typography variant="label1" color="grey.90" component="div">
              Choose which should also move contacts already waiting:
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {waitingDelays.map((node) => (
                <Box
                  key={node.nodeId}
                  sx={(theme) => ({
                    borderLeft: `2px solid ${theme.palette.grey[50]}`,
                    pl: 2,
                  })}
                >
                  <Typography variant="label1" color="grey.90" component="div">
                    <NodeLabel>{node.label}</NodeLabel> —{" "}
                    {formatDelaySummary(node.previousData)} →{" "}
                    {formatDelaySummary(node.currentData)} ·{" "}
                    {formatNumber(node.occupancy)} waiting
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={moveWaitingNodeIds.includes(node.nodeId)}
                        onChange={(e) =>
                          toggleMoveWaiting(node.nodeId, e.target.checked)
                        }
                        disabled={isPublishing}
                      />
                    }
                    label={
                      <Typography variant="label1" color="grey.90">
                        Also move contacts already waiting
                      </Typography>
                    }
                  />
                </Box>
              ))}
            </Stack>
            <Alert variant="outlined" severity="info" sx={{ mt: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="label1" color="inherit">
                  Unchecked: waiting contacts keep their original time; only new
                  arrivals use the new settings.
                </Typography>
                <Typography variant="label1" color="inherit">
                  Checked: waiting contacts recalculate from the new settings.
                  If their new time has passed, they continue right away.
                </Typography>
                <Typography variant="label1" color="inherit">
                  Contacts that entered before this update was released always
                  keep their original time.
                </Typography>
              </Stack>
            </Alert>
          </Section>
        ) : (
          <Alert variant="outlined" severity="info" sx={{ mt: 2 }}>
            <Typography variant="label1" color="inherit">
              Changing a delay does not shorten the wait for contacts already
              waiting on that step. They finish the wait they started and pick
              up the updated workflow at their next step.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          color="primary"
          onClick={onCancel}
          disabled={isPublishing}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={publish}
          disabled={isPublishing}
        >
          {isPublishing ? "Publishing…" : "Publish"}
        </Button>
      </DialogActions>
    </>
  );
};

export default WorkflowPublishConfirmationDialogContent;
